import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Select, MenuItem, FormControl } from '@mui/material'
import styles from './ResponsiveTable.module.scss'

/**
 * TableHeader - Shared header bar for responsive tables.
 * Shows title, result count, action buttons, and a sort dropdown on mobile.
 */
const TableHeader = ({
  title,
  icon,
  resultCount,
  resultLabel,
  actions,
  isCompact,
  columns,
  sortField,
  sortDirection,
  onSort
}) => {
  const sortableColumns = (columns || []).filter((c) => c.sortable)

  const handleSortChange = (e) => {
    if (onSort) onSort(e.target.value)
  }

  const getSortLabel = () => {
    const col = sortableColumns.find((c) => c.id === sortField)
    const label = col ? col.label : sortField
    const dir = sortDirection === 'asc' ? '(oldest)' : '(newest)'
    return `${label} ${dir}`
  }

  return (
    <div className={styles.tableHeader}>
      <div className={styles.tableHeader__left}>
        {icon && <span className={styles.tableHeader__icon}>{icon}</span>}
        {title && (
          <Typography className={styles.tableHeader__title}>
            {title}
          </Typography>
        )}
        {resultCount > 0 && (
          <span className={styles.tableHeader__count}>
            {resultCount} {resultLabel || 'results'}
          </span>
        )}
      </div>

      <div className={styles.tableHeader__right}>
        {/* Mobile sort dropdown */}
        {isCompact && sortableColumns.length > 0 && onSort && (
          <FormControl size='small' className={styles.tableHeader__sortControl}>
            <Select
              value={sortField || ''}
              onChange={handleSortChange}
              displayEmpty
              className={styles.tableHeader__sortSelect}
              renderValue={() => (
                <span className={styles.tableHeader__sortValue}>
                  Sort: {getSortLabel()}
                </span>
              )}
            >
              {sortableColumns.map((col) => (
                <MenuItem key={col.id} value={col.id}>
                  {col.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Action buttons (export, reset, etc.) */}
        {actions && (
          <Box className={styles.tableHeader__actions}>
            {actions}
          </Box>
        )}
      </div>
    </div>
  )
}

TableHeader.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
  resultCount: PropTypes.number,
  resultLabel: PropTypes.string,
  actions: PropTypes.node,
  isCompact: PropTypes.bool,
  columns: PropTypes.array,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func
}

export default React.memo(TableHeader)
